import { useParams } from "react-router-dom";
import {
  BrandingProvider,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardShell,
  formatLapTime,
  leaderboardCarLabels,
} from "@vms/leaderboard-ui";
import {
  useBrandingTemplate,
  useEvent,
  useEventRaceStandings,
} from "../../api/leaderboards";

/**
 * Chrome-less race standings for cloud-ui — mirror of hub-ui's
 * `apps/hub-ui/src/pages/display/RaceDisplay.tsx`. Uses the same
 * `@vms/leaderboard-ui` components, just with cloud-ui hooks for data + WS.
 */
export function RaceDisplay() {
  const { eventId } = useParams<{ eventId: string }>();

  const { data: event } = useEvent(eventId);
  const venueId = event?.venueId ?? undefined;
  const { data: branding } = useBrandingTemplate(event?.brandingTemplateId ?? null);
  const { data: standings } = useEventRaceStandings(venueId, eventId);

  const entries = standings ?? [];
  const carLabels = leaderboardCarLabels(entries, event?.carsAvailable);

  return (
    <BrandingProvider branding={branding}>
      <LeaderboardShell
        title={event?.name ?? "Race Standings"}
        subtitle={event?.trackName ?? event?.track}
        mode="race"
      >
        {entries.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-lg">
            Race not started.
          </div>
        ) : (
          <LeaderboardList
            totalCount={entries.length}
            renderEntry={(index) => {
              const entry = entries[index];
              const gapStr =
                entry.gapToLeaderMs == null
                  ? ""
                  : `+${formatLapTime(entry.gapToLeaderMs)}`;
              const extraColumns = [
                { label: "Laps", value: String(entry.lapsCompleted) },
                { label: "Gap", value: gapStr },
                { label: "Last", value: entry.lastLapTimeMs ? formatLapTime(entry.lastLapTimeMs) : "—" },
                { label: "Best", value: entry.bestLapTimeMs ? formatLapTime(entry.bestLapTimeMs) : "—" },
              ];
              return (
                <LeaderboardRow
                  position={entry.position}
                  driverName={`${entry.driver.firstName} ${entry.driver.lastName}`}
                  companyName={entry.driver.companyName || undefined}
                  car={carLabels[index]}
                  lapTime={formatLapTime(entry.totalTimeMs)}
                  showSectors={false}
                  extraColumns={extraColumns}
                />
              );
            }}
          />
        )}
      </LeaderboardShell>
    </BrandingProvider>
  );
}
